import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Snackbar from 'utils/snackbar';

const MAX_ALT_FILENAME_LENGTH = 32;

const getFileName = (src: string, altText: string, mimeType?: string) => {
  const extension = !mimeType
    ? 'png'
    : mimeType === 'image/jpeg'
      ? 'jpg'
      : mimeType === 'image/svg+xml'
        ? 'svg'
        : mimeType.split('/')[1] || 'png';
  const urlFileName = src.split('?')[0].split('#')[0].split('/').pop()?.trim();
  if (urlFileName) {
    const uuid = urlFileName.slice(0, 8);
    return `generated-image-${uuid}.${extension}`;
  }
  const cleanAlt = altText
    .trim()
    .replace(/([.\s]|[^a-z0-9_-])+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, MAX_ALT_FILENAME_LENGTH);
  if (cleanAlt)
    return cleanAlt.includes('.') ? cleanAlt : `${cleanAlt}.${extension}`;
  return `image.${extension}`;
};
const escapeAttr = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
const buildClipboardData = ({
  src,
  altText,
  titleText,
  imageBlob,
  mimeType
}: {
  src: string;
  altText: string;
  titleText: string;
  imageBlob?: Blob;
  mimeType?: string;
}) => {
  const attrs = [`src="${escapeAttr(src)}"`];
  if (altText) attrs.push(`alt="${escapeAttr(altText)}"`);
  if (titleText) attrs.push(`title="${escapeAttr(titleText)}"`);
  const data: Record<string, Blob> = {
    'text/plain': new Blob([src], { type: 'text/plain' }),
    'text/html': new Blob([`<img ${attrs.join(' ')} />`], { type: 'text/html' })
  };

  if (imageBlob && mimeType?.startsWith('image/')) {
    data[mimeType] =
      imageBlob.type === mimeType
        ? imageBlob
        : new Blob([imageBlob], { type: mimeType });
    return { data, includesImage: true };
  }

  return { data, includesImage: false };
};
const resolveImageMimeType = (headerType?: string, blobType?: string) => {
  const normalizedHeaderType = headerType?.split(';')[0].trim();
  if (normalizedHeaderType?.startsWith('image/')) return normalizedHeaderType;
  return blobType?.startsWith('image/') ? blobType : undefined;
};
const useImageActions = ({
  src,
  altText,
  titleText,
  onInfo
}: {
  src: string;
  altText: string;
  titleText: string;
  onInfo?: (event: React.MouseEvent) => void;
}) => {
  const handleDownload = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!src) {
        Snackbar.error('Image source is missing');
        return;
      }

      const openInNewTab = () => {
        const link = document.createElement('a');
        link.href = src;
        link.target = '_blank';
        link.rel = 'noreferrer';
        link.download = getFileName(src, altText);
        document.body.appendChild(link);
        link.click();
        link.remove();
      };

      fetch(src)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const fileName = getFileName(src, altText, blob.type);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.rel = 'noreferrer';
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          openInNewTab();
        });
    },
    [altText, src]
  );

  const handleCopy = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!src) {
        Snackbar.error('Image source is missing');
        return;
      }

      const writeClipboard = (imageBlob?: Blob, mimeType?: string) => {
        if (!navigator.clipboard?.write || !window.ClipboardItem) {
          return Promise.reject(
            new Error('Clipboard image copy not supported')
          );
        }
        const { data, includesImage } = buildClipboardData({
          src,
          altText,
          titleText,
          imageBlob,
          mimeType
        });
        return navigator.clipboard
          .write([new ClipboardItem(data)])
          .then(() => includesImage);
      };

      const writeTextFallback = () =>
        navigator.clipboard?.writeText
          ? navigator.clipboard.writeText(src).then(() => false)
          : Promise.reject(new Error('Clipboard text copy not supported'));

      fetch(src)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }
          const headerType = response.headers.get('content-type') || undefined;
          return response.blob().then((blob) => ({ blob, headerType }));
        })
        .then(({ blob, headerType }) => {
          const mimeType = resolveImageMimeType(headerType, blob.type);
          return writeClipboard(blob, mimeType);
        })
        .catch(() => writeClipboard().catch(() => writeTextFallback()))
        .catch(() => {
          Snackbar.error('Failed to copy image');
        });
    },
    [altText, src, titleText]
  );

  const handleInfo = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onInfo?.(e);
    },
    [onInfo]
  );

  const actions = [
    {
      title: 'Copy',
      icon: <ContentCopyIcon fontSize="xsmall" />,
      handler: handleCopy
    },
    {
      title: 'Download',
      icon: <DownloadIcon fontSize="xsmall" />,
      handler: handleDownload
    }
  ];
  if (altText?.trim()) {
    actions.unshift({
      title: 'Info',
      icon: <InfoOutlinedIcon fontSize="xsmall" />,
      handler: handleInfo
    });
  }
  return actions;
};
export default useImageActions;
